import { useMemo } from 'react';
import { CheckCheck, Bell } from 'lucide-react';
import { PanelFrame } from '../panel-frame.js';
import { PanelEmptyState } from '../panel-empty-state.js';
import {
  useAppStore,
  usePanelEntityId,
  useSetPanelEntity,
  type PanelId,
  type TabId,
} from '../store.js';
import { useDockviewApi } from '../dockview-context.js';
import type { PanelParams, PanelComponentKey } from '../components.js';
import { useAlerts, useMarkAlertsRead } from '../../api/hooks.js';
import type { AlertItem } from '../../api/client.js';

// Alerts — the proactive-monitoring inbox. The daily compute.alerts worker
// diffs every watchlisted company against its prior snapshot and writes a
// per-user feed of material changes (Signal moves, accounts overdue, new
// charges, status). Click an alert to broadcast the company to a linked
// Record; double-click to pop a floating Record. Email digests are sent in
// parallel when the keys are provisioned, but this feed is the source of truth.

type Tone = 'up' | 'down' | 'flag' | 'neutral';

const TYPE_META: Record<string, { label: string; tone: Tone }> = {
  seller_readiness_up: { label: 'Seller Readiness Score ↑', tone: 'up' },
  seller_readiness_down: { label: 'Seller Readiness Score ↓', tone: 'down' },
  origination_up: { label: 'Origination ↑', tone: 'up' },
  origination_down: { label: 'Origination ↓', tone: 'down' },
  accounts_overdue: { label: 'Accounts overdue', tone: 'flag' },
  new_charge: { label: 'New charge', tone: 'flag' },
  status_change: { label: 'Status', tone: 'neutral' },
};

function metaFor(type: string): { label: string; tone: Tone } {
  return TYPE_META[type] ?? { label: type.replace(/_/g, ' '), tone: 'neutral' };
}

function relTime(iso: string): string {
  const then = new Date(iso).getTime();
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}mo`;
  return `${Math.round(mo / 12)}y`;
}

function dayBucket(iso: string): string {
  const d = new Date(iso).getTime();
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (d >= startToday) return 'Today';
  if (d >= startToday - 86_400_000) return 'Yesterday';
  if (d >= startToday - 7 * 86_400_000) return 'This week';
  return 'Earlier';
}

export function AlertsPanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const { data, isLoading, isError } = useAlerts();
  const markRead = useMarkAlertsRead();
  const selected = usePanelEntityId(tabId, panelId) ?? null;
  const select = useSetPanelEntity(tabId, panelId);

  const alerts = data?.data ?? [];
  const unread = data?.unread ?? 0;

  // Group the (already newest-first) feed under day headers.
  const groups = useMemo(() => {
    const out: Array<{ bucket: string; items: AlertItem[] }> = [];
    for (const a of alerts) {
      const bucket = dayBucket(a.createdAt);
      const last = out[out.length - 1];
      if (last && last.bucket === bucket) last.items.push(a);
      else out.push({ bucket, items: [a] });
    }
    return out;
  }, [alerts]);

  // Double-click drill → a floating Record pinned to the company (mirrors Referrals).
  const apiRef = useDockviewApi();
  const addPanel = useAppStore((s) => s.actions.addPanel);
  const setPanelEntity = useAppStore((s) => s.actions.setPanelEntity);
  function openEntity(entityId: string) {
    const api = apiRef.current;
    if (!api) return;
    const newPanelId = addPanel(tabId, { component: 'company-overview', title: 'Record' });
    const cascade = api.panels.filter((p) => p.api.location.type === 'floating').length;
    const offset = 24 * cascade;
    api.addPanel<PanelParams>({
      id: newPanelId,
      component: 'company-overview' as PanelComponentKey,
      title: 'Record',
      params: { tabId, panelId: newPanelId },
      floating: { position: { left: 120 + offset, top: 90 + offset }, width: 760, height: 620 },
    });
    setPanelEntity(tabId, newPanelId, entityId);
  }

  function onRowClick(a: AlertItem) {
    select(a.entityId);
    if (!a.readAt) markRead.mutate({ ids: [a.id] });
  }

  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div className="al-root">
        <style>{AL_CSS}</style>

        <div className="al-toolbar">
          <span className="al-count">
            {unread > 0 ? (
              <>
                <span className="al-unreaddot" /> {unread} unread
              </>
            ) : (
              `${alerts.length} alert${alerts.length === 1 ? '' : 's'}`
            )}
          </span>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="al-btn"
            disabled={unread === 0 || markRead.isPending}
            onClick={() => markRead.mutate({ all: true })}
            title="Mark every alert as read"
          >
            <CheckCheck size={12} /> Mark all read
          </button>
        </div>

        <div className="al-listwrap">
          {isError ? (
            <div className="al-empty">Couldn’t load alerts.</div>
          ) : isLoading ? (
            <div className="al-empty">Loading…</div>
          ) : alerts.length === 0 ? (
            <PanelEmptyState fill icon={<Bell size={18} />} title="No alerts yet">
              Add companies to a watchlist and we’ll flag material changes — Signal moves, overdue
              accounts, new charges, status changes — here each day.
            </PanelEmptyState>
          ) : (
            groups.map((g) => (
              <div key={g.bucket} className="al-group">
                <div className="al-grouphead">{g.bucket}</div>
                {g.items.map((a) => {
                  const m = metaFor(a.type);
                  return (
                    <div
                      key={a.id}
                      role="button"
                      tabIndex={0}
                      className={
                        'al-row' +
                        (a.readAt ? '' : ' unread') +
                        (selected === a.entityId ? ' sel' : '')
                      }
                      onClick={() => onRowClick(a)}
                      onDoubleClick={() => openEntity(a.entityId)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') onRowClick(a);
                      }}
                    >
                      <span className={`al-chip al-${m.tone}`}>{m.label}</span>
                      <div className="al-body">
                        <div className="al-line1">
                          <span className="al-name">{a.name ?? 'A tracked company'}</span>
                          {a.subsegment && <span className="al-sub">· {a.subsegment}</span>}
                        </div>
                        <div className="al-line2">
                          <span className="al-title">{a.title}</span>
                          {a.detail && <span className="al-detail"> · {a.detail}</span>}
                        </div>
                      </div>
                      <span className="al-time">{relTime(a.createdAt)}</span>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </PanelFrame>
  );
}

const AL_CSS = `
.al-root { display:flex; flex-direction:column; height:100%; font-size:var(--t-body); font-variant-numeric:tabular-nums; }
.al-toolbar { display:flex; align-items:center; gap:var(--s-3); padding:var(--s-2) var(--s-3); border-bottom:1px solid var(--hairline); }
.al-count { display:inline-flex; align-items:center; gap:6px; color:var(--text-secondary); font-size:var(--t-body); }
.al-unreaddot { width:7px; height:7px; border-radius:50%; background:var(--accent); display:inline-block; }
.al-btn { display:inline-flex; align-items:center; gap:4px; font:inherit; font-size:var(--t-body); font-weight:400; color:var(--text-primary); background:var(--bg); border:1px solid var(--hairline); border-radius:2px; padding:2px 8px; cursor:pointer; }
.al-btn:hover:not(:disabled) { background:var(--hover-subtle); }
.al-btn:disabled { opacity:.45; cursor:default; }
.al-listwrap { flex:1; overflow:auto; min-height:0; }
.al-empty { padding:var(--s-4); color:var(--text-tertiary); }
.al-empty-h { font-weight:600; color:var(--text-secondary); margin-bottom:4px; }
.al-empty-b { max-width:46ch; line-height:1.45; }
.al-group { }
.al-grouphead { position:sticky; top:0; z-index:1; padding:4px var(--s-3); background:var(--surface-elevated); border-bottom:1px solid var(--hairline); font-size:var(--t-meta); font-weight:700; letter-spacing:.04em; text-transform:uppercase; color:var(--text-tertiary); }
.al-row { display:grid; grid-template-columns:auto 1fr auto; align-items:start; gap:var(--s-3); padding:8px var(--s-3); border-bottom:1px solid var(--border-subtle); cursor:pointer; }
.al-row:hover { background:var(--hover-subtle); }
.al-row.sel { background:var(--color-accent-100); }
.al-row.unread { background:color-mix(in srgb, var(--accent) 5%, transparent); }
.al-row.unread .al-name::before { content:''; display:inline-block; width:6px; height:6px; border-radius:50%; background:var(--accent); margin-right:6px; vertical-align:middle; }
.al-chip { align-self:start; margin-top:1px; display:inline-flex; align-items:center; white-space:nowrap; height:18px; padding:0 7px; border-radius:3px; font-size:var(--t-meta); font-weight:700; letter-spacing:-0.01em; }
.al-chip.al-up { background:color-mix(in srgb, var(--positive) 14%, transparent); color:var(--positive); }
.al-chip.al-down { background:color-mix(in srgb, var(--negative) 14%, transparent); color:var(--negative); }
.al-chip.al-flag { background:color-mix(in srgb, #9a6a00 16%, transparent); color:#7a5400; }
.al-chip.al-neutral { background:var(--surface-elevated); color:var(--text-secondary); border:1px solid var(--hairline); }
.al-body { min-width:0; }
.al-line1 { display:flex; align-items:baseline; gap:5px; min-width:0; }
.al-name { font-weight:600; color:var(--text-primary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.al-sub { color:var(--text-tertiary); font-size:var(--t-meta); white-space:nowrap; }
.al-line2 { color:var(--text-secondary); line-height:1.35; margin-top:1px; }
.al-title { color:var(--text-primary); }
.al-detail { color:var(--text-tertiary); font-family:var(--font-mono, monospace); }
.al-time { color:var(--text-tertiary); font-size:var(--t-meta); white-space:nowrap; padding-top:1px; }
`;
